import React, {useEffect} from "react"
import {createRoot} from "react-dom/client"
import {ThemeProvider, createTheme, alpha, lighten, darken} from "@mui/material/styles"
import Routes from "./utils/Routes"
import "./index.css"
import {blue} from "@mui/material/colors"
import {CimpressAuth} from "./services/utilities/authClient"
import {getAccessToken} from "./utils/auth"
import {QueryClient, QueryClientProvider} from "@tanstack/react-query"
import {ToastRegion} from "@cimpress-ui/react"
import "@cimpress-ui/react/styles.css"
import "@commercehub/components/index.esm.css"
import "@commercehub/shared/index.esm.css"
import "@commercehub/order/index.esm.css"
import {getEmailFromAccessToken} from "./utils/auth"

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			staleTime: 5 * 60 * 1000, // 5 min — treat data as fresh, skip needless refetches
		},
	},
})
const theme = createTheme({
	palette: {
		primary: blue,
		secondary: {
			main: "#008000",
		},
	},
	typography: {
		fontFamily: ["Open Sans", "sans-serif"].join(","),
		body1: {
			fontSize: "14px",
		},
		h5: {
			fontSize: "1.25rem",
		},
		h6: {
			fontSize: "1rem",
		},
		subtitle1: {
			fontSize: ".75rem",
		},
		subtitle2: {
			fontSize: "14px",
		},
	},
	components: {
		MuiButton: {
			styleOverrides: {
				containedSecondary: {
					backgroundColor: "#e6e6e6",
					color: "black",
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					textTransform: "none",
				},
			},
		},
		MuiMenuItem: {
			styleOverrides: {
				root: {
					fontSize: ".875rem",
				},
			},
		},
		MuiTable: {
			styleOverrides: {
				root: {
					fontFamily: ["Open Sans", "sans-serif"].join(","),
					fontSize: ".875rem",
					fontWeight: 400,
				},
			},
		},
	},
})
;(theme as any).alpha = alpha
;(theme as any).lighten = lighten
;(theme as any).darken = darken
export function App() {
	const accessToken = getAccessToken()
	localStorage.setItem("accessToken", accessToken)
	const email = getEmailFromAccessToken()
	if (email) {
		localStorage.setItem("profile", JSON.stringify({email}))
	}
	useEffect(() => {
		const preventAriaHijack = (event: FocusEvent) => {
			if (event.relatedTarget === null) {
				event.stopPropagation()
				event.stopImmediatePropagation()
			}
		}
		window.addEventListener("focusout", preventAriaHijack, true)
		return () => {
			window.removeEventListener("focusout", preventAriaHijack, true)
		}
	}, [])
	return (
		<QueryClientProvider client={queryClient}>
			<div data-cim-style-root>
				<ToastRegion />
				<ThemeProvider theme={theme}>
					<Routes />
				</ThemeProvider>
			</div>
		</QueryClientProvider>
	)
}

const urlParams = new URLSearchParams(window.location.search)
const shopperId = urlParams.get("shopper_id")
if (shopperId) {
	sessionStorage.setItem("pre_auth_url", window.location.href)
}

CimpressAuth.ensureAuthentication(window.location)
	.then((isAuthenticated: boolean) => {
		if (isAuthenticated) {
			const savedUrl = sessionStorage.getItem("pre_auth_url")
			if (savedUrl) {
				sessionStorage.removeItem("pre_auth_url")
				if (savedUrl !== window.location.href) {
					window.location.replace(savedUrl)
					return
				}
			}
			const container = document.getElementById("root")
			const root = createRoot(container!)
			root.render(<App />)
		}
	})
	.catch((error: unknown) => {
		const message =
			typeof error === "object" && error !== null && "message" in error
				? String((error as {message?: unknown}).message ?? "")
				: String(error ?? "")

		const isKnownTransitionIssue = /invalid state|transition was aborted/i.test(message)

		sessionStorage.removeItem("pre_auth_url")

		if (isKnownTransitionIssue) {
			console.warn("Handled auth transition rejection", {
				message,
				href: window.location.href,
				error,
			})
			return
		}

		console.error("Unhandled auth bootstrap error", {
			message,
			href: window.location.href,
			error,
		})
	})
	.catch((error: unknown) => {
		const message =
			typeof error === "object" && error !== null && "message" in error
				? String((error as {message?: unknown}).message ?? "")
				: String(error ?? "")

		const isKnownTransitionIssue = /invalid state|transition was aborted/i.test(message)

		sessionStorage.removeItem("pre_auth_url")

		if (isKnownTransitionIssue) {
			console.warn("Handled auth transition rejection", {
				message,
				href: window.location.href,
				error,
			})
			return
		}

		console.error("Unhandled auth bootstrap error", {
			message,
			href: window.location.href,
			error,
		})
	})
